import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AirlineSeatReclineNormalIcon from '@mui/icons-material/AirlineSeatReclineNormal';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import StarIcon from '@mui/icons-material/Star';

// Create a custom axios instance with the correct base URL
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token') || 'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJodHRwOi8vbG9jYWxob3N0IiwiaWF0IjoxNzQyMTE2NjQ2LCJleHAiOjE3NDIxMjAyNDYsIm5iZiI6MTc0MjExNjY0NiwianRpIjoiNk9YZWNxSGNlYTdoUTc4OSIsInN1YiI6IjEiLCJwcnYiOiIyM2JkNWM4OTQ5ZjYwMGFkYjM5ZTcwMWM0MDA4NzJkYjdhNTk3NmY3In0.y3kGqgZq41qAPBhDxucZgn0pngEt6_D79gXBRsaj6PM'}`
  }
});

const TourVehicleInfo = ({ tourId }) => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (tourId) {
      fetchTourVehicles();
    }
  }, [tourId]);

  const fetchTourVehicles = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/api/v1/tours/${tourId}/vehicles`);
      
      if (response.data && response.data.data) {
        setVehicles(response.data.data);
      } else {
        setError(response.data?.message || 'Failed to fetch tour vehicles');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred while fetching tour vehicles');
      console.error('Error fetching tour vehicles:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAccordionChange = () => {
    setExpanded(!expanded);
  };

  if (loading) {
    return (
      <div className="p-6">
        <h3 className="text-xl font-bold mb-6">Tour Vehicles</h3>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <h3 className="text-xl font-bold mb-6">Tour Vehicles</h3>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </div>
    );
  }

  if (!vehicles || vehicles.length === 0) {
    return (
      <div className="p-6">
        <h3 className="text-xl font-bold mb-6">Tour Vehicles</h3>
        <Alert severity="info" sx={{ mb: 2 }}>
          No vehicles are currently assigned to this tour.
        </Alert>
      </div>
    );
  }

  // Find the default vehicle
  const defaultVehicle = vehicles.find(v => v.pivot?.is_default) || vehicles[0];
  const otherVehicles = vehicles.filter(v => v.id !== defaultVehicle.id);

  return (
    <div className="p-6">
      <h3 className="text-xl font-bold mb-6">Tour Vehicles</h3>
      
      {/* Default/Primary Vehicle */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <DirectionsCarIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="subtitle1" component="div">
              Primary Vehicle
            </Typography>
            {defaultVehicle.pivot?.is_default && (
              <Chip 
                icon={<StarIcon />} 
                label="Recommended" 
                color="primary" 
                size="small" 
                sx={{ ml: 1 }} 
              />
            )}
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4} md={3}>
              <CardMedia
                component="img"
                sx={{ 
                  width: '100%',
                  height: 160,
                  objectFit: 'cover',
                  borderRadius: 1
                }}
                image={defaultVehicle.image || 'https://via.placeholder.com/300x160?text=Vehicle'}
                alt={defaultVehicle.name}
              />
            </Grid>
            
            <Grid item xs={12} sm={8} md={9}>
              <Typography variant="h6" component="div">
                {defaultVehicle.name}
              </Typography>
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {defaultVehicle.make} {defaultVehicle.model} ({defaultVehicle.model_year})
              </Typography>
              
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <DirectionsCarIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      {defaultVehicle.type}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={6} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AirlineSeatReclineNormalIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      {defaultVehicle.capacity} passengers
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              
              {defaultVehicle.features && defaultVehicle.features.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                    Features:
                  </Typography>
                  <Grid container spacing={1}>
                    {Array.isArray(defaultVehicle.features) ? 
                      defaultVehicle.features.map((feature, index) => (
                        <Grid item xs={6} md={4} key={index}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CheckIcon fontSize="small" sx={{ mr: 0.5, color: 'success.main' }} />
                            <Typography variant="body2">{feature}</Typography>
                          </Box>
                        </Grid>
                      )) : 
                      <Grid item xs={12}>
                        <Typography variant="body2">{defaultVehicle.features}</Typography>
                      </Grid>
                    }
                  </Grid>
                </Box>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      {/* Other Available Vehicles */}
      {otherVehicles.length > 0 && (
        <Accordion expanded={expanded} onChange={handleAccordionChange}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="other-vehicles-content"
            id="other-vehicles-header"
          >
            <Typography>
              {otherVehicles.length} Other {otherVehicles.length === 1 ? 'Vehicle' : 'Vehicles'} Available
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {otherVehicles.map((vehicle) => (
                <Grid item xs={12} md={6} key={vehicle.id}>
                  <Card sx={{ display: 'flex', height: '100%' }}>
                    <CardMedia
                      component="img"
                      sx={{ 
                        width: 120,
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      image={vehicle.image || 'https://via.placeholder.com/120x120?text=Vehicle'}
                      alt={vehicle.name}
                    />
                    <CardContent sx={{ flex: '1 0 auto' }}>
                      <Typography component="div" variant="subtitle1">
                        {vehicle.name}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {vehicle.type} • {vehicle.capacity} passengers
                      </Typography>
                      
                      {vehicle.features && vehicle.features.length > 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Features: {Array.isArray(vehicle.features) ? vehicle.features.join(', ') : vehicle.features}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}
      
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          All tours include a professional driver. Vehicle availability may vary based on booking date and group size.
        </Typography>
      </Box>
    </div>
  );
};

export default TourVehicleInfo;
